/**
 * FantaSquad come app sul telefono (Andrea 2026-10-08).
 *
 * - `InstallaAppBanner`: in Home, solo su telefono e solo se NON siamo gia'
 *   nell'app installata. Su Android/Chrome apre direttamente la finestra di
 *   installazione; altrimenti mostra la guida. "Non ora" lo nasconde per un
 *   mese (si ritrova sempre nel Profilo).
 * - `GuidaInstallazione`: i passi per iOS (Safari → Condividi → Aggiungi
 *   alla schermata Home) e Android (Installa app), piu' il link all'APK se
 *   Andrea lo pubblica (VITE_APK_URL).
 */

import { useState } from "react";
import {
  inStandalone,
  piattaforma,
  safariSuIos,
  URL_APK,
  useInstallazione,
} from "../../lib/installazione";
import { tr } from "../../i18n";

const CHIAVE_NASCOSTO = "fs-installa-nascosto";
// Andrea 2026-10-08: "Non ora" nasconde la striscia per 3 giorni, non un mese
const DURATA_NON_ORA_MS = 3 * 24 * 60 * 60 * 1000;

function nascostoDiRecente(): boolean {
  try {
    const v = Number(localStorage.getItem(CHIAVE_NASCOSTO) ?? 0);
    return Date.now() - v < DURATA_NON_ORA_MS;
  } catch {
    return false;
  }
}

function IconaCondividiIos() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="inline w-4 h-4 align-[-3px]"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 3v12" />
      <path d="m8 7 4-4 4 4" />
      <path d="M5 11v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8" />
    </svg>
  );
}

function Passo({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="shrink-0 w-6 h-6 rounded-full bg-accent text-ink font-mono text-xs font-bold flex items-center justify-center">
        {n}
      </span>
      <span className="text-sm text-fg leading-relaxed">{children}</span>
    </li>
  );
}

function PassiIos() {
  return (
    <ol className="space-y-2.5">
      <Passo n={1}>
        {tr("Apri")} <span className="font-bold">fantasquad.it</span> con{" "}
        <span className="font-bold">Safari</span>{" "}
        {tr("(da Chrome o altri browser iPhone non si può).")}
      </Passo>
      <Passo n={2}>
        {tr("Tocca il pulsante")} <span className="font-bold">{tr("Condividi")}</span>{" "}
        <IconaCondividiIos /> {tr("in basso al centro (il quadrato con la freccia verso l'alto).")}
      </Passo>
      <Passo n={3}>
        {tr("Scorri l'elenco e tocca")}{" "}
        <span className="font-bold">{tr("Aggiungi alla schermata Home")}</span>.
      </Passo>
      <Passo n={4}>
        {tr("Conferma con")} <span className="font-bold">{tr("Aggiungi")}</span>
        {tr(": l'icona di FantaSquad compare tra le tue app.")}
      </Passo>
    </ol>
  );
}

function PassiAndroid({ onInstalla }: { onInstalla?: () => void }) {
  return (
    <div className="space-y-3">
      {onInstalla && (
        <button type="button" onClick={onInstalla} className="btn-primary w-full">
          {tr("📲 Installa FantaSquad")}
        </button>
      )}
      <ol className="space-y-2.5">
        <Passo n={1}>
          {tr("Apri")} <span className="font-bold">fantasquad.it</span> con{" "}
          <span className="font-bold">Chrome</span>.
        </Passo>
        <Passo n={2}>
          {tr("Tocca i tre puntini")} <span className="font-bold">⋮</span> {tr("in alto a destra.")}
        </Passo>
        <Passo n={3}>
          {tr("Tocca")} <span className="font-bold">{tr("Installa app")}</span> (su alcuni telefoni:{" "}
          <span className="font-bold">{tr("Aggiungi a schermata Home")}</span>) e conferma.
        </Passo>
      </ol>
      {URL_APK && (
        <div className="rounded-xl border border-primary/30 bg-primary-deep/40 p-3 space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-fg-muted">
            {tr("In alternativa: l'app Android (APK)")}
          </div>
          <a href={URL_APK} className="btn-ghost w-full" download>
            {tr("⬇️ Scarica l'APK")}
          </a>
          <div className="text-[11px] text-fg-muted">
            {tr(
              tr(
                "Alla prima installazione Android chiede di consentire le app da questa origine: è normale, l'APK è pubblicato da noi.",
              ),
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** La guida completa, con la piattaforma giusta davanti. */
export function GuidaInstallazione() {
  const { installabile, installa } = useInstallazione();
  const p = piattaforma();
  const [aperto, setAperto] = useState<"ios" | "android">(p === "android" ? "android" : "ios");

  if (inStandalone()) {
    return (
      <div className="text-sm text-fg">
        {tr("✅ Stai già usando FantaSquad come app: icona in Home e schermo intero.")}
      </div>
    );
  }

  const linguette = (
    <div className="flex gap-1 p-1 rounded-xl bg-primary-deep/70 border border-primary/30">
      {(
        [
          ["ios", tr(" iPhone / iPad")],
          ["android", tr("🤖 Android")],
        ] as const
      ).map(([k, label]) => (
        <button
          key={k}
          type="button"
          onClick={() => setAperto(k)}
          className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-bold transition ${
            aperto === k ? "bg-accent text-ink shadow" : "text-fg-muted hover:text-fg"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="space-y-3">
      <p className="text-sm text-fg-muted">
        Aggiunta alla schermata Home, FantaSquad si apre a schermo intero con la sua icona, come
        un'app vera: niente barra del browser e un tocco per entrare.
        {p === "altro" && tr(" Apri fantasquad.it dal telefono e segui i passi.")}
      </p>
      {p === "ios" && !safariSuIos() && (
        <div className="rounded-xl border border-warning/50 bg-warning/10 p-3 text-xs text-fg">
          {tr("⚠️ Su iPhone l'installazione funziona solo da")}{" "}
          <span className="font-bold">Safari</span>
          {tr(": apri fantasquad.it lì e ripeti i passi.")}
        </div>
      )}
      {p === "altro" && linguette}
      {(p === "ios" || (p === "altro" && aperto === "ios")) && <PassiIos />}
      {(p === "android" || (p === "altro" && aperto === "android")) && (
        <PassiAndroid onInstalla={installabile ? () => void installa() : undefined} />
      )}
    </div>
  );
}

/** Striscia in Home su telefono: "FantaSquad come app". */
export function InstallaAppBanner() {
  const { installabile, installa, appenaInstallata } = useInstallazione();
  const [nascosto, setNascosto] = useState(nascostoDiRecente);
  const [guida, setGuida] = useState(false);
  const p = piattaforma();

  if (p === "altro" || inStandalone() || nascosto || appenaInstallata) return null;

  const nascondi = () => {
    try {
      localStorage.setItem(CHIAVE_NASCOSTO, String(Date.now()));
    } catch {
      /* ignora */
    }
    setNascosto(true);
  };

  const azione = async () => {
    if (installabile) {
      const ok = await installa();
      if (ok) return;
    }
    setGuida(true);
  };

  return (
    <>
      <div className="md:hidden rounded-card border border-accent/40 bg-gradient-to-r from-primary-deep to-bg-card p-3 flex items-center gap-3">
        <img src="/icon-192.png" alt="" className="w-11 h-11 rounded-xl shrink-0" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="font-display text-sm text-fg">{tr("FantaSquad come app")}</div>
          <div className="text-[11px] text-fg-muted leading-snug">
            {tr("Icona sul telefono e schermo intero, senza browser.")}
          </div>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <button
            type="button"
            onClick={() => void azione()}
            className="btn-primary text-xs px-3 py-1.5"
          >
            {installabile ? tr("Installa") : tr("Come fare")}
          </button>
          <button
            type="button"
            onClick={nascondi}
            className="text-[11px] text-fg-muted hover:text-fg text-center"
          >
            {tr("Non ora")}
          </button>
        </div>
      </div>

      {guida && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4"
          onClick={() => setGuida(false)}
        >
          <div
            className="bg-bg-card border border-accent/30 sm:rounded-2xl rounded-t-2xl w-full max-w-md max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-primary/30">
              <h3 className="font-display text-lg text-fg">{tr("📲 Installa FantaSquad")}</h3>
              <button
                type="button"
                onClick={() => setGuida(false)}
                className="w-9 h-9 rounded-full border border-primary/40 text-fg-muted hover:text-fg"
                aria-label={tr("Chiudi")}
              >
                ✕
              </button>
            </div>
            <div className="px-5 py-4">
              <GuidaInstallazione />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
